import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Manages scroll position across navigation:
 * - PUSH (clicking links): scrolls to top
 * - POP (back/forward button): restores saved scroll position
 */
const ScrollToTop = () => {
  const { pathname, key } = useLocation();
  const navType = useNavigationType();
  const scrollPositions = useRef({});
  const prevKey = useRef(key);

  // Save scroll position before navigating away
  useEffect(() => {
    const saveScroll = () => {
      scrollPositions.current[prevKey.current] = window.scrollY;
    };

    // Save on every scroll (debounced via the ref)
    const handleScroll = () => {
      scrollPositions.current[prevKey.current] = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      saveScroll();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (navType === 'POP') {
      // Back/forward button — restore saved scroll position
      const savedY = scrollPositions.current[key];
      if (savedY !== undefined) {
        // Wait for content to render, then restore
        const restore = () => {
          window.scrollTo(0, savedY);
        };
        // Try multiple times as async content loads
        requestAnimationFrame(restore);
        setTimeout(restore, 100);
        setTimeout(restore, 300);
        setTimeout(restore, 600);
      }
    } else if (navType === 'PUSH') {
      // Forward navigation — scroll to top
      window.scrollTo(0, 0);
    }

    // Update the key ref for the new page
    prevKey.current = key;
  }, [pathname, key, navType]);

  return null;
};

export default ScrollToTop;
